// src/services/audioService.ts
// Singleton audio manager. LRU cache (max 5 HTMLAudioElement objects).
// React integration: subscribe(cb) / unsubscribe. Used by useAudio.ts.
//
// URL resolution: callers pass audioUrl directly (read from
// useExerciseStore.unitMediaCache[unitId].audioUrl) to avoid a circular
// import with the store.
//
// Ported from the audioSystem object in exercise-manager.js (lines 44–410).
// Key differences from the original:
//   • No DOM manipulation — state is pushed to listeners, not to DOM nodes.
//   • SW prefetch via fetch() (fire-and-forget), not postMessage.
//   • CLEAR_AUDIO_CACHE postMessage preserved from original on clearAll().

const MAX_CACHED   = 5;
const SEEK_STEP_S  = 5;
const POLL_INTERVAL_MS = 250;

// ── Public types ─────────────────────────────────────────────────────────────

export interface AudioServiceState {
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  isSeeking: boolean;
  currentUnitId: string | null;
}

export type AudioStateListener = (state: AudioServiceState) => void;

// ── Service class ─────────────────────────────────────────────────────────────

class AudioService {
  // LRU cache — Map preserves insertion order, used for eviction.
  private _cache: Map<string, HTMLAudioElement> = new Map();
  private _current: HTMLAudioElement | null = null;
  private _currentUnitId: string | null = null;
  private _isPlaying = false;
  private _currentTime = 0;
  private _duration = 0;
  private _isSeeking = false;
  private _pollTimer: ReturnType<typeof setInterval> | null = null;
  private _listeners: Set<AudioStateListener> = new Set();

  // ── Subscription ──────────────────────────────────────────────────────

  /** Subscribe to state changes. Returns unsubscribe function. */
  subscribe(cb: AudioStateListener): () => void {
    this._listeners.add(cb);
    // Emit current snapshot immediately so hook can initialize synchronously.
    cb(this._snap());
    return () => this._listeners.delete(cb);
  }

  private _snap(): AudioServiceState {
    return {
      isPlaying: this._isPlaying,
      currentTime: this._currentTime,
      duration: this._duration,
      isSeeking: this._isSeeking,
      currentUnitId: this._currentUnitId,
    };
  }

  private _emit(): void {
    const snap = this._snap();
    this._listeners.forEach(cb => cb(snap));
  }

  // ── Load ──────────────────────────────────────────────────────────────

  /**
   * Loads and caches an Audio element for a unit.
   * Returns the cached element immediately if already loaded (readyState ≥ 2).
   */
  async loadAudio(unitId: string, audioUrl: string): Promise<HTMLAudioElement | null> {
    if (!unitId || !audioUrl) return null;

    // Serve from cache if already loaded.
    const cached = this._cache.get(unitId);
    if (cached && cached.readyState >= 2) return cached;

    try {
      const audio = new Audio(audioUrl);
      audio.crossOrigin = 'anonymous';
      audio.preload = 'metadata';
      audio.volume = 0.7;

      // Wait for metadata so duration is available.
      await new Promise<void>((resolve) => {
        const onMeta = () => { audio.removeEventListener('loadedmetadata', onMeta); resolve(); };
        audio.addEventListener('loadedmetadata', onMeta);
        audio.addEventListener('error', () => resolve(), { once: true });
      });

      // Update LRU position: delete + re-insert moves to end (newest).
      this._cache.delete(unitId);
      this._cache.set(unitId, audio);
      this._evict();

      // Fire-and-forget SW prefetch so the browser caches the audio file.
      try { fetch(audioUrl, { mode: 'no-cors' }).catch(() => {}); } catch {}

      return audio;
    } catch (err) {
      console.warn('[audioService] loadAudio failed:', err);
      return null;
    }
  }

  // ── LRU eviction ──────────────────────────────────────────────────────

  private _evict(): void {
    if (this._cache.size <= MAX_CACHED) return;
    // Evict the oldest entry that is not currently playing (Map insertion order).
    for (const [key, el] of this._cache) {
      if (key !== this._currentUnitId) {
        el.pause();
        el.src = '';           // release media resource
        this._cache.delete(key);
        console.log(`[audioService] evicted stale audio for ${key}`);
        break;
      }
    }
  }

  // ── Play / Pause ──────────────────────────────────────────────────────

  async playPause(unitId: string, audioUrl: string): Promise<void> {
    const audio = await this.loadAudio(unitId, audioUrl);
    if (!audio) return;

    if (this._isPlaying && this._currentUnitId === unitId) {
      // Pause current
      audio.pause();
      this._isPlaying = false;
      this._stopPoll();
    } else {
      // Stop whatever is playing
      this._stopAll(false); // don't reset currentTime of the new unit

      this._current = audio;
      this._currentUnitId = unitId;

      try {
        await audio.play();
        this._isPlaying = true;
        this._startPoll(audio);

        audio.onended = () => {
          this._isPlaying = false;
          this._currentTime = 0;
          this._stopPoll();
          this._emit();
        };
      } catch (err) {
        console.warn('[audioService] play() rejected:', err);
        this._isPlaying = false;
      }
    }
    this._emit();
  }

  // ── Seek controls ─────────────────────────────────────────────────────

  seekBackward(): void {
    if (!this._current) return;
    this._current.currentTime = Math.max(0, this._current.currentTime - SEEK_STEP_S);
    this._currentTime = this._current.currentTime;
    this._emit();
  }

  seekForward(): void {
    if (!this._current) return;
    const dur = this._current.duration || 0;
    this._current.currentTime = Math.min(dur, this._current.currentTime + SEEK_STEP_S);
    this._currentTime = this._current.currentTime;
    this._emit();
  }

  /** Seek to a position by percentage (0–100). */
  seekTo(percent: number): void {
    if (!this._current || !this._current.duration) return;
    this._isSeeking = true;
    this._current.currentTime = (Math.min(100, Math.max(0, percent)) / 100) * this._current.duration;
    this._currentTime = this._current.currentTime;
    this._isSeeking = false;
    this._emit();
  }

  // ── Stop all ──────────────────────────────────────────────────────────

  private _stopAll(resetCurrentTime: boolean): void {
    this._stopPoll();
    this._cache.forEach(audio => { audio.pause(); });
    if (resetCurrentTime) {
      this._cache.forEach(audio => { audio.currentTime = 0; });
    }
    this._isPlaying = false;
    this._current = null;
    this._currentUnitId = null;
    this._currentTime = 0;
  }

  /** Public stop — pauses all audio and resets state. */
  stopAll(): void {
    this._stopAll(true);
    this._emit();
  }

  // ── Progress polling ──────────────────────────────────────────────────

  private _startPoll(audio: HTMLAudioElement): void {
    this._stopPoll();
    this._pollTimer = setInterval(() => {
      if (audio && !audio.paused) {
        this._currentTime = audio.currentTime;
        this._duration    = audio.duration || 0;
        this._emit();
      }
    }, POLL_INTERVAL_MS);
  }

  private _stopPoll(): void {
    if (this._pollTimer !== null) {
      clearInterval(this._pollTimer);
      this._pollTimer = null;
    }
  }

  // ── Logout cleanup ────────────────────────────────────────────────────

  /** Call on user logout. Stops audio, clears cache, notifies SW. */
  clearAll(): void {
    this._stopAll(true);
    this._cache.clear();
    try {
      navigator.serviceWorker?.controller?.postMessage({ type: 'CLEAR_AUDIO_CACHE' });
    } catch {}
    this._emit();
  }
}

// ── Singleton export ──────────────────────────────────────────────────────────

export const audioService = new AudioService();
export default audioService;